import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  Pagination,
  Popover,
  Select,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-react";
import {
  OmsProduct,
  OmsReport,
  OmsReportPart,
  Stage,
} from "../../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { fileSize } from "./omsReportFormatting";

const pageSizeKey = "oms-report-files-per-page";
const statuses = {
  idle: "Not requested",
  preparing: "Preparing",
  sent: "Sent to browser",
  failed: "Failed",
};
type RequestStatus = keyof typeof statuses;
function initialPageSize() {
  try {
    const saved = Number(localStorage.getItem(pageSizeKey));
    return [10, 25, 50].includes(saved) ? saved : 10;
  } catch {
    return 10;
  }
}

function compareFileNumber(a: OmsReportPart, b: OmsReportPart) {
  return Number(a.id) - Number(b.id);
}
function fileColumns(
  requests: Record<string, RequestStatus>,
  pendingId: string | undefined,
  onDownload: (part: OmsReportPart) => void
): TableProps.ColumnDefinition<OmsReportPart>[] {
  const columns: TableProps.ColumnDefinition<OmsReportPart>[] = [
    {
      id: "file",
      header: "File",
      sortingComparator: compareFileNumber,
      cell: (part) => (
        <Popover
          triggerType="custom"
          dismissAriaLabel="Close file details"
          header={`File ${part.id}`}
          content={
            <span className="oms-report-download__filename">
              {part.fileName}
            </span>
          }
        >
          <Button
            variant="inline-link"
            ariaLabel={`Filename for file ${part.id}`}
          >
            File {part.id}
          </Button>
        </Popover>
      ),
    },
    {
      id: "rows",
      header: "Rows",
      sortingField: "rowCount",
      cell: (part) => part.rowCount.toLocaleString(),
    },
    {
      id: "size",
      header: "Size",
      sortingField: "sizeBytes",
      cell: (part) => fileSize(part.sizeBytes),
    },
    {
      id: "status",
      header: "Request status",
      cell: (part) => statuses[requests[part.id] || "idle"],
    },
    {
      id: "download",
      header: "Download",
      cell: (part) => (
        <div className="oms-report-download__file-action">
          <Button
            ariaLabel={`Download CSV file ${part.id}`}
            iconName="download"
            wrapText={false}
            fullWidth
            loading={pendingId === part.id}
            loadingText={`Preparing CSV file ${part.id}`}
            disabled={Boolean(pendingId) && pendingId !== part.id}
            onClick={() => onDownload(part)}
          >
            Download
          </Button>
        </div>
      ),
    },
  ];
  return columns;
}

export default function OMSReportFiles({
  report,
  product,
}: {
  report: OmsReport;
  product: OmsProduct;
}) {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [requests, setRequests] = useState<Record<string, RequestStatus>>({});
  const [sort, setSort] = useState<TableProps.SortingColumn<OmsReportPart>>({
    sortingComparator: compareFileNumber,
  });
  const [descending, setDescending] = useState(false);
  const download = useMutation({
    mutationFn: async (part: OmsReportPart) =>
      (
        await OmsReportsApiFactory().getOmsReportPartDownload(
          product,
          Stage.Prod,
          report.snapshotId,
          part.id
        )
      ).data,
    onMutate: (part) =>
      setRequests((previous) => ({ ...previous, [part.id]: "preparing" })),
    onSuccess: (result, part) => {
      setRequests((previous) => ({ ...previous, [part.id]: "sent" }));
      window.location.assign(result.url);
    },
    onError: (_error, part) =>
      setRequests((previous) => ({ ...previous, [part.id]: "failed" })),
  });
  const needle = search.trim().toLowerCase();
  const number = needle.match(/^(?:file\s+)?(\d+)$/)?.[1];
  const parts = report.parts
    .filter(
      (part) =>
        (number
          ? part.id === number.replace(/^0+(?=\d)/, "")
          : part.fileName.toLowerCase().includes(needle)) &&
        (statusFilter === "all" ||
          (requests[part.id] || "idle") === statusFilter)
    )
    .sort((a, b) => {
      const result = sort.sortingComparator
        ? sort.sortingComparator(a, b)
        : Number(a[sort.sortingField as "rowCount" | "sizeBytes"]) -
          Number(b[sort.sortingField as "rowCount" | "sizeBytes"]);
      return descending ? -result : result;
    });
  const pages = Math.max(1, Math.ceil(parts.length / pageSize));
  const current = Math.min(page, pages);
  const first = (current - 1) * pageSize;
  const columns = fileColumns(
    requests,
    download.isLoading ? download.variables?.id : undefined,
    (part) => download.mutate(part)
  );
  return (
    <SpaceBetween size="s">
      <div className="oms-report-download__file-toolbar">
        <TextFilter
          filteringText={search}
          filteringAriaLabel="Find CSV files"
          filteringPlaceholder="File number or filename"
          filteringClearAriaLabel="Clear file search"
          countText={`${parts.length.toLocaleString()} ${
            parts.length === 1 ? "match" : "matches"
          }`}
          onChange={({ detail }) => {
            setSearch(detail.filteringText);
            setPage(1);
          }}
        />
        <Select
          ariaLabel="Filter file request status"
          selectedOption={{
            value: statusFilter,
            label:
              statusFilter === "all"
                ? "All request statuses"
                : statuses[statusFilter as RequestStatus],
          }}
          options={[
            { value: "all", label: "All request statuses" },
            ...Object.entries(statuses).map(([value, label]) => ({
              value,
              label,
            })),
          ]}
          onChange={({ detail }) => {
            setStatusFilter(detail.selectedOption.value || "all");
            setPage(1);
          }}
        />
      </div>
      <div className="oms-report-download__pagination">
        <Box color="text-body-secondary">
          {parts.length
            ? `Files ${(first + 1).toLocaleString()}–${Math.min(
                first + pageSize,
                parts.length
              ).toLocaleString()} of ${parts.length.toLocaleString()}`
            : "0 files"}
        </Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Pagination
            currentPageIndex={current}
            pagesCount={pages}
            ariaLabels={{
              nextPageLabel: "Next page of CSV files",
              previousPageLabel: "Previous page of CSV files",
              pageLabel: (value) => `CSV files page ${value}`,
            }}
            onChange={({ detail }) => setPage(detail.currentPageIndex)}
          />
          <CollectionPreferences
            title="File display preferences"
            confirmLabel="Apply"
            cancelLabel="Cancel"
            preferences={{ pageSize }}
            pageSizePreference={{
              title: "Files per page",
              options: [10, 25, 50].map((value) => ({
                value,
                label: `${value} files`,
              })),
            }}
            onConfirm={({ detail }) => {
              const size = detail.pageSize || 10;
              if (![10, 25, 50].includes(size)) return;
              setPageSize(size);
              setPage(1);
              try {
                localStorage.setItem(pageSizeKey, String(size));
              } catch {
                // Display preferences remain usable without local storage.
              }
            }}
          />
        </SpaceBetween>
      </div>
      {download.isError && (
        <Alert
          type="error"
          header={`Could not prepare file ${download.variables?.id || ""}`}
        >
          Choose Download again to retry.
        </Alert>
      )}
      {download.isLoading && (
        <div role="status">Preparing file {download.variables?.id}…</div>
      )}
      {download.isSuccess && (
        <div role="status">
          File {download.variables?.id} was sent to your browser. Check your
          browser’s downloads for progress.
        </div>
      )}
      <div className="oms-report-download__table">
        <Table
          ariaLabels={{ tableLabel: "Scheduled report CSV files" }}
          contentDensity="compact"
          variant="embedded"
          trackBy="id"
          items={parts.slice(first, first + pageSize)}
          columnDefinitions={columns}
          sortingColumn={sort}
          sortingDescending={descending}
          onSortingChange={({ detail }) => {
            setSort(detail.sortingColumn);
            setDescending(Boolean(detail.isDescending));
            setPage(1);
          }}
          empty={
            <Box textAlign="center" padding="m">
              <SpaceBetween size="s">
                <Box fontWeight="bold">No matching files</Box>
                <Box color="text-body-secondary">
                  Try a different file number, filename or request status.
                </Box>
                <Button
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setPage(1);
                  }}
                >
                  Clear file filters
                </Button>
              </SpaceBetween>
            </Box>
          }
        />
      </div>
    </SpaceBetween>
  );
}
